import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Heart, Brain, Wind } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function MeditationGuide() {
  const navigate = useNavigate();

  const steps = [
    {
      step: 1,
      title: "Find Your Space",
      description: "Choose a quiet spot where you won't be disturbed. This could be a corner of your bedroom, a comfortable chair, or even your car parked in a quiet spot. The location matters less than the consistency.",
      tip: "Consider creating a small 'meditation corner' with a comfortable cushion and perhaps a candle or plant to signal to your brain that this is your peaceful space."
    },
    {
      step: 2,
      title: "Get Comfortable",
      description: "Sit in a position that's comfortable but alert. You can sit cross-legged on the floor, in a chair with feet flat, or even lie down if sitting is uncomfortable. Keep your spine naturally straight.",
      tip: "If sitting on the floor, place a cushion under your hips to tilt your pelvis slightly forward—this makes it easier to maintain good posture."
    },
    {
      step: 3,
      title: "Set Your Timer",
      description: "Start with 10 minutes. As you build your practice, you can extend to 15-20 minutes. Using a timer frees you from checking the clock and wondering how much time has passed.",
      tip: "Use a gentle alarm tone—a harsh buzzer can be jarring after deep relaxation."
    },
    {
      step: 4,
      title: "Close Your Eyes & Breathe",
      description: "Gently close your eyes or soften your gaze toward the floor. Take three deep breaths to transition from 'doing mode' to 'being mode.' Then let your breath return to its natural rhythm.",
      tip: "Notice where you feel your breath most prominently—nostrils, chest, or belly. This becomes your anchor point."
    },
    {
      step: 5,
      title: "Focus on Your Breath",
      description: "Simply observe each inhale and exhale. You don't need to control or change your breathing—just notice it. When your mind wanders (and it will), gently guide your attention back to your breath.",
      tip: "Counting breaths can help maintain focus: count '1' on the inhale, '2' on the exhale, up to 10, then start over."
    },
    {
      step: 6,
      title: "Be Kind to Your Wandering Mind",
      description: "Your mind will wander—this is completely normal and not a sign of failure. Each time you notice your mind has drifted and bring it back, you're actually strengthening your meditation muscle.",
      tip: "Think of thoughts like clouds passing through the sky. Acknowledge them, let them drift by, and return to your breath."
    }
  ];

  const benefits = [
    { icon: Brain, title: "Mental Clarity", description: "Reduces mental clutter and improves focus and decision-making" },
    { icon: Heart, title: "Emotional Balance", description: "Decreases anxiety and depression while increasing emotional resilience" },
    { icon: Wind, title: "Stress Relief", description: "Lowers cortisol levels and activates your body's relaxation response" },
    { icon: Clock, title: "Better Sleep", description: "Calms the nervous system, leading to deeper, more restorative sleep" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-4 text-secondary-700 hover:text-primary"
          >
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
            Week 1 • Day 4 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Mindfulness Meditation for Beginners
          </h1>
          <p className="text-xl text-secondary-600">
            A Simple 10-Minute Practice to Transform Your Day
          </p>
        </div>

        {/* Hero Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b182c6e0-1353-47f6-8a97-d98fdf4ac183/images/8eba373478d81af82c68e3539d40318f65a5de1f433978a77956edc062533a1c.png"
            alt="Woman Meditating"
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Introduction */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
            Why Meditation Matters in Your 40s
          </h2>
          <p className="text-secondary-700 mb-4">
            Your 40s often bring increased responsibilities—career demands, aging parents, growing children, and the pressure to "have it all together." Meditation isn't about escaping these realities; it's about building the mental resilience to navigate them with grace.
          </p>
          <p className="text-secondary-700 mb-4">
            Research shows that just <strong>10 minutes of daily meditation can reduce anxiety by 30%</strong>, lower blood pressure, improve sleep quality, and even slow cellular aging. Think of it as a daily reset button for your nervous system.
          </p>
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
            <p className="text-secondary-800 font-medium">
              "Meditation is not about stopping thoughts, but recognizing that we are more than our thoughts and our feelings." — Arianna Huffington
            </p>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="grid md:grid-cols-2 gap-4 mb-6">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <benefit.icon className="w-10 h-10 text-purple-500 mb-3" />
              <h3 className="font-bold text-secondary-900 mb-2">{benefit.title}</h3>
              <p className="text-sm text-secondary-600">{benefit.description}</p>
            </div>
          ))}
        </section>

        {/* Step by Step */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">
            Your 10-Minute Meditation Practice
          </h2>
          <div className="space-y-6">
            {steps.map((item, index) => (
              <div key={index} className="border-l-4 border-purple-300 pl-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-bold text-sm">
                    {item.step}
                  </span>
                  <h3 className="text-lg font-bold text-secondary-900">{item.title}</h3>
                </div>
                <p className="text-secondary-700 mb-3">{item.description}</p>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-sm text-purple-800">
                    <strong>Pro Tip:</strong> {item.tip}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Common Challenges */}
        <section className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border-2 border-purple-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">
            Common Challenges (And How to Overcome Them)
          </h2>
          <div className="space-y-4">
            {[
              {
                challenge: "\"I can't stop my thoughts!\"",
                solution: "You're not supposed to. The goal isn't an empty mind—it's awareness. Every time you notice a thought and return to your breath, you're succeeding."
              },
              {
                challenge: "\"I don't have time.\"",
                solution: "You have 10 minutes. Wake up 10 minutes earlier, meditate during lunch, or replace 10 minutes of scrolling. The time exists—it's about prioritizing."
              },
              {
                challenge: "\"I keep falling asleep.\"",
                solution: "Try meditating earlier in the day, sitting upright instead of lying down, or keeping your eyes slightly open with a soft gaze."
              },
              {
                challenge: "\"I feel restless and uncomfortable.\"",
                solution: "This is normal, especially at first. Start with just 5 minutes and gradually increase. Movement before meditation (like stretching) can also help."
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 shadow-sm">
                <p className="font-bold text-secondary-900 mb-2">{item.challenge}</p>
                <p className="text-secondary-600 text-sm">{item.solution}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
            Ready to Begin?
          </h2>
          <p className="text-secondary-600 mb-6">
            Set a timer for 10 minutes, find a comfortable position, and give yourself permission to simply be. 
            There's no perfect meditation—there's only your meditation. Start today.
          </p>
          <div className="bg-purple-100 rounded-xl p-4 inline-block">
            <p className="text-purple-800 font-medium">
              "The best time to plant a tree was 20 years ago. The second best time is now."
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}

export default MeditationGuide;
